import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { Tag } from '../../../tag/Tag';
import { Text } from '../../../text';
import type { CascaderOption } from '../helpers';
import { getTagLabel } from '../utils/display-utils';

/**
 * カスケーダートリガーコンテンツのプロパティ
 */
type CascaderTriggerContentProps = {
  /** 複数選択モードかどうか */
  multiple: boolean;
  /** 単一選択時の表示テキスト */
  displayText: string;
  /** プレースホルダーテキスト */
  placeholder: string;
  /** 表示するタグのパスリスト */
  visibleTags: string[][];
  /** 残りのタグ数（省略された数） */
  remainingCount: number;
  /** 全てのカスケーダーオプション */
  options: CascaderOption[];
  /** タグ削除時のコールバック */
  onRemoveTag: (path: string[]) => void;
  /** クリアボタンが表示されているかどうか */
  shouldShowClearButton: boolean;
};

/**
 * カスケーダーのトリガー表示コンポーネント
 *
 * ドロップダウンを開くためのトリガー部分を表示します。
 * 単一選択モードではテキスト、複数選択モードではタグを表示します。
 *
 * @param props - CascaderTriggerContentProps
 * @returns JSX.Element
 */
export const CascaderTrigger = ({
  multiple,
  displayText,
  placeholder,
  visibleTags,
  remainingCount,
  options,
  onRemoveTag,
  shouldShowClearButton,
}: CascaderTriggerContentProps) => {
  return (
    <>
      <div className={cn('flex flex-1 gap-1', multiple ? 'flex-wrap' : 'overflow-hidden')}>
        {multiple ? (
          visibleTags.length > 0 ? (
            <>
              {visibleTags.map((path) => {
                const label = getTagLabel(path, options);
                return (
                  <Tag
                    style={{ maxWidth: '200px' }}
                    key={JSON.stringify(path)}
                    onRemove={() => onRemoveTag(path)}
                  >
                    {label}
                  </Tag>
                );
              })}
              {remainingCount > 0 && (
                <Text
                  as="span"
                  variant="description"
                  className="inline-flex items-center rounded bg-secondary px-2 py-0.5"
                >
                  +{remainingCount}
                </Text>
              )}
            </>
          ) : (
            <Text as="span" color="muted">
              {placeholder}
            </Text>
          )
        ) : (
          <Text
            as="span"
            color={!displayText ? 'muted' : undefined}
            className="overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {displayText || placeholder}
          </Text>
        )}
      </div>
      {!shouldShowClearButton && <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
    </>
  );
};
